<template>
  <div>
    <div v-if="thread && thread.id">
      <div class="drawer-heading">
        <h3>{{ this.thread.booking.name }}</h3>
      </div>
      <div class="drawer-body">
        <div class="sidebar">
          <button v-if="$userIsAdmin && this.thread.active" @click="handleCloseThreadCallback" class="drawer-button">
            <i class="fa fa-envelope fa-fw" title="Close Thread"></i>
            Shutdown Thread
          </button>
          <button v-else-if="$userIsAdmin" @click="handleReopenThreadCallback" class="drawer-button">
            <i class="fa fa-envelope-open fa-fw" title="Reopen Thread"></i>
            Reopen Thread
          </button>
          <div v-if="$userIsAdmin">
            <button v-if="!thread.pinned" @click="handlePinThreadCallback" class="drawer-button">
                <i class="fa fa-thumb-tack fa-fw"></i>
                Pin
            </button>
            <button v-else @click="handleUnpinThreadCallback" class="drawer-button">
                <i class="fa fa-thumb-tack fa-fw"></i>
                Unpin
            </button>
            <br/>
          </div>
          <h5><strong>PARTICIPANTS</strong></h5>
          <div
            v-for="participant in this.thread.participants"
            :key="participant.id">
            <p v-if="!$checkUserIsAdmin(participant)">
              <img :src="participant.avatar" alt="">
              {{ participant.name }} ({{ $t(participant.role) }})
            </p>
          </div>
          <p>
            <img :src="$vlastMode ? '/img/vlast-avatar-128.png' : '/img/pickstar-avatar-128.png'" alt="">
            {{ clientName }} Admin
          </p>
          <br/>
          <div v-if="threadFiles">
            <h5>
              <strong>BOOKING FILES</strong>
              <button
                class="btn btn-xs btn-primary rounded-button"
                type="submit"
                @click="handleGetFileInputSidebar">
                Upload Files
              </button>
              <input style="display: none;" ref="fileInputSidebar" type="file" multiple
                     @change="this.handleAddBookingFiles">
            </h5>
            <ul style="padding: 0;" v-if="threadFiles.length">
              <booking-file-list-item v-for="file in threadFiles" :key="file.id" :file="file" :booking="thread.booking">
              </booking-file-list-item>
              <pickstar-loading v-if="loading" class="m-t-sm"/>
            </ul>
            <pickstar-loading v-else-if="loading" class="m-t-sm"/>
            <no-results v-else-if="threadFiles">
              There are currently no files.
            </no-results>
          </div>
          <h5><strong>BOOKING DETAILS</strong></h5>

          <div class="row" v-if="$userIsAdmin">
            <div class="col-xs-4">
              <p><strong>Booking ID</strong></p>
            </div>
            <div class="col-xs-8">
              <p> {{ this.thread.booking.id }}</p>
            </div>
          </div>

          <div class="row">
            <div class="col-xs-4">
              <p v-if="$costReferences"><strong>{{ $t('budget') | sentenceCase }}</strong></p>
              <p v-else><strong>Value</strong></p>
            </div>
            <div class="col-xs-8">
              <p v-if="$userIsTalent || $userIsManager">
                {{ this.thread.booking.budget_per_star_as_user_rate | currency(this.thread.booking.currency) | nullable }}
                <span v-if="$userIsManager">
                /{{ $starLabel }}
              </span>
              </p>
              <p v-else>
                <span v-if="$costReferences">
                  {{ this.thread.booking.budget_per_star | currency(this.thread.booking.currency) }}
                </span>

                <span v-if="$userIsAdmin && this.thread.booking.currency !== 'points'">
                  ({{ this.thread.booking.budget_per_star_as_user_rate | currency(this.thread.booking.currency) }})
                </span>
                <span>
                {{ budgetPostText }}
                </span>
              </p>
            </div>
          </div>

          <div class="row">
            <div class="col-xs-4">
              <p><strong>Request Details</strong></p>
            </div>
            <div class="col-xs-8">
              <p style="overflow-y: hidden; display: -webkit-box; max-height: 10vh; -webkit-line-clamp: 4;-webkit-box-orient: vertical; text-overflow: ellipsis;">
                <span id="description">{{ this.thread.booking.description }}</span>
                <tooltip target="#description" :text="this.thread.booking.description"></tooltip>
              </p>
            </div>
          </div>

          <div class="row">
            <div class="col-xs-4">
              <p><strong>Requirements</strong></p>
            </div>
            <div class="col-xs-8">
              <p style="overflow-y: hidden; display: -webkit-box; max-height: 10vh; -webkit-line-clamp: 4;-webkit-box-orient: vertical; text-overflow: ellipsis;">
                <span id="requirements">{{ this.thread.booking.requirements }}</span>
                <tooltip target="#requirements" :text="this.thread.booking.requirements"></tooltip>
              </p>
            </div>
          </div>

          <div class="row" v-if="thread.booking.address">
            <div class="col-xs-4">
              <p><strong>Location</strong></p>
            </div>
            <div class="col-xs-8">
              <p>{{ thread.booking.address | nullable }}</p>
            </div>
          </div>

          <div class="row" v-if="this.thread.booking.date">
            <div class="col-xs-4">
              <p><strong>Event Date</strong></p>
            </div>
            <div class="col-xs-8">
              <p>{{ this.thread.booking.date | date($user.date_format_preference) }}</p>
            </div>
          </div>

          <div class="row">
            <div class="col-xs-4">
              <p><strong>Status</strong></p>
            </div>
            <div class="col-xs-8">
              <p v-if="this.thread.booking.status === 'scheduled'"><span class="messaging-tag mt-orange">{{ this.thread.booking.status }} <i class="fa fa-check"></i></span></p>
              <p v-if="this.thread.booking.status === 'booked'"><span class="messaging-tag mt-green">{{ this.thread.booking.status }} <i class="fa fa-check"></i></span></p>
              <p v-if="this.thread.booking.status === 'completed'"><span class="messaging-tag mt-green">{{ this.thread.booking.status }} <i class="fa fa-check"></i></span></p>
              <p v-if="this.thread.booking.status === 'cancelled'"><span class="messaging-tag mt-red">{{ this.thread.booking.status }} <i class="fa fa-cross"></i></span></p>
            </div>
          </div>

          <router-link class="messaging-flex-box" v-if="this.$route !== 'bookings.show'" :to="{ name: 'bookings.show', params: { id: this.thread.booking.id } }" href="#drawer-messages-chat" data-toggle="drawer" aria-foldedopen="true" aria-controls="drawer-messages-chat">
            <a class="btn btn-primary btn-sm rounded-button" @click="viewBooking">View Booking</a><br/>
          </router-link>
          <hr/>
          <div v-if="this.thread.booking.payments && this.thread.booking.payments.length > 0">
            <h5 v-if="$userIsAdmin || $userIsClient"><strong>PAYMENT DETAILS</strong></h5>

            <div class="row" v-if="userCanSeePaymentStatus">
              <div class="col-xs-4">
                <p><strong>Deposit</strong></p>
              </div>
              <div class="col-xs-8">
                <p><span class="messaging-tag" :class="paymentClass(this.thread.booking.payments[0].status)">{{ this.thread.booking.payments[0].status }}</span></p>
              </div>
            </div>
            <div class="row" >
              <div class="col-xs-4">
                <p v-if="this.thread.booking.payments[0].paid_date && ($userIsAdmin || $userIsClient)">
                  <strong>Paid Date </strong>
                </p>
                <p v-else-if="($userIsAdmin || $userIsClient)">
                  <strong>Due Date  </strong>
                </p>
              </div>
              <div class="col-xs-8">
                <p v-if="this.thread.booking.payments[0].paid_date && ($userIsAdmin || $userIsClient)">
                  {{ this.thread.booking.payments[0].paid_date | date($user.date_format_preference) }}
                </p>
                <p v-else-if="($userIsAdmin || $userIsClient)">
                  {{ this.thread.booking.payments[0].due_date | date($user.date_format_preference) }}
                </p>
              </div>
            </div>

            <div class="row" v-if="userCanSeePaymentStatus">
              <div class="col-xs-4">
                <p><strong>Final</strong></p>
              </div>
              <div class="col-xs-8" v-if="this.thread.booking.payments.length > 1">
                <p><span class="messaging-tag" :class="paymentClass(this.thread.booking.payments[1].status)">{{ this.thread.booking.payments[1].status }}</span></p>
              </div>
            </div>
            <div class="row" v-if="this.thread.booking.payments.length > 1">
              <div class="col-xs-4">
                <p v-if="this.thread.booking.payments[1].paid_date && ($userIsAdmin || $userIsClient)">
                  <strong>Paid Date </strong>
                </p>
                <p v-else-if="($userIsAdmin || $userIsClient)">
                  <strong>Due Date </strong>
                </p>
              </div>
              <div class="col-xs-8">
                <p v-if="this.thread.booking.payments[1].paid_date && ($userIsAdmin || $userIsClient)">
                  {{ this.thread.booking.payments[1].paid_date | date($user.date_format_preference) }}
                </p>
                <p v-else-if="($userIsAdmin || $userIsClient)">
                  {{ this.thread.booking.payments[1].due_date | date($user.date_format_preference) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <pickstar-loading class="m-t-lg"/>
    </div>
  </div>
</template>
<script>
import BookingFileListItem from '../booking/BookingFileListItem'
import { mapState, mapActions } from 'vuex'
import LazyLoading from 'codium-lazy-loading'
import { notify } from 'utils'
import { helpers, budgetHelpers } from 'mixins/bookings'
import { ChatTracker } from 'mixins/analytics'

export default {
  mixins: [helpers, budgetHelpers, ChatTracker],

  components: {
    LazyLoading,
    BookingFileListItem
  },

  computed: {
    ...mapState('message', ['thread']),
    ...mapState('clientConfig', ['client_config']),
    ...mapState('booking/file', ['files', 'loading']),

    userCanSeePaymentStatus (status) {
      if (this.$userIsTalent || this.$userIsManager) {
        return status === 'paid'
      }
      return true
    },

    threadFiles () {
      return this.files.filter(element => element.thread_id === this.thread.id || element.thread_id === null)
    }
  },

  methods: {
    ...mapActions('message', ['closeThread', 'reopenThread', 'clearThread', 'pinThread', 'unpinThread']),
    ...mapActions('booking/file', ['storeBookingFile', 'setFilesLoading']),

    viewBooking () {
      this.trackEvent(this.analytics.V2.ChatOverviewViewEvent)
      this.clearThread(this.client_config.slug)
    },

    adminDisplayed () {
      return '/img/profile-default.jpg'
    },

    handlePinThreadCallback () {
      return this.pinThread({ threadId: this.thread.id })
    },

    handleUnpinThreadCallback () {
      return this.unpinThread({ threadId: this.thread.id })
    },

    handleCloseThreadCallback () {
      return this.closeThread({ threadId: this.thread.id })
    },

    handleReopenThreadCallback () {
      return this.reopenThread({ threadId: this.thread.id })
    },

    paymentClass (status) {
      switch (status) {
        case 'unpaid':
          return 'mt-red'
        case 'sent':
          return 'mt-orange'
        case 'paid':
          return 'mt-green'
      }
    },

    /*
     * Opens the file input window
     *
     * @return void
     */
    handleGetFileInputSidebar () {
      this.$refs.fileInputSidebar.value = null
      this.$refs.fileInputSidebar.click()
    },

    handleAddBookingFiles ({ target: { files } }) {
      if (files.length !== 0) {
        for (let i = 0; i < files.length; i++) {
          if (files[i].size >= 15000000) {
            notify.error('Maximum file size is 15MB. Unable to upload file: ' + files[i].name)
          } else {
            this.storeBookingFile({ id: this.thread.booking.id, file: files[i], threadId: this.thread.id })
              .catch(() => {
                this.setFilesLoading(false)
                notify.error('Something went wrong uploading your file. Please try again.')
              })
          }
        }
      }
    }
  }
}
</script>
